/**
 * ASSISTANT DE CLÔTURE — L'ÉTAT DE CHAQUE ÉTAPE, SANS NAVIGATEUR.
 *
 * Le rail (`ClosingRail.tsx`) dessine ; ce fichier décide. Il est pur, sans
 * React ni réseau : les tests l'interrogent directement, et une règle aussi
 * sensible que « ce bar est-il compté ? » ne doit pas dépendre d'un rendu.
 *
 * ═══ QUATRE ÉTATS, LUS DANS LES DONNÉES, JAMAIS DANS LA NAVIGATION ═══
 *
 *   ✓ faite       — le point de vente est compté, le périmètre déclaré… ;
 *   ● en cours    — l'étape affichée, tant qu'elle n'est pas faite ;
 *   ○ à faire     — le reste ;
 *   ! écart       — un écart OUVERT, ou un écart au-delà du seuil d'alerte
 *                   qu'aucune explication ne couvre encore.
 *
 * Une étape n'est pas « faite » parce qu'on l'a dépassée : l'ancienne
 * progression cochait tout ce qui précédait l'étape courante, si bien qu'un
 * bar jamais compté apparaissait terminé dès qu'on avait cliqué plus loin.
 *
 * Et un point de vente n'est pas « fait » parce que son périmètre est
 * déclaré : `step_done` passe à vrai dès qu'on répond « ouvert / fermé »
 * (`setPosOpen`). « Fait » = CHACUNE de ses lignes comptée ; une partie
 * seulement = « compté en partie », et pas encore d'alerte (recette du
 * 07/10/2026 : Bar ROOM 2, jamais compté, s'affichait coché). Ni « fait »
 * tant que des espèces comptées ne sont portées par aucune ligne : la
 * finalisation ne les retiendrait pas, et le serveur la refuse
 * (`especesAReporter`, night-sheet-model ; revue du 07/10/2026).
 *
 * ═══ LES OFFERTS NE SONT NI ANNONCÉS, NI COMPTÉS, NI EN ÉCART ═══
 *
 * La ligne OFFERT (carte serveur) n'est pas une recette : elle n'entre ni
 * dans l'annoncé — qui doit égaler le tableau « Ce que la nuit annonce »,
 * hors offerts —, ni dans le compté, ni dans l'écart. Et l'écart ne porte
 * que sur les lignes COMPTÉES : un terminal pas encore saisi n'est pas un
 * manque de 4 918 €.
 *
 * ═══ LES CASES DU HAUT D'UN POINT DE VENTE, EN DIRECT ═══
 *
 * `chiffresPointDeVente` applique ces mêmes règles à ce qui est EN TRAIN
 * d'être saisi : les tickets TPE et le comptage des espèces vivent dans
 * l'étape tant qu'ils ne sont pas enregistrés. Les cases « Ce que vous avez
 * compté » et l'écart suivent la frappe, sans attendre l'aller-retour serveur
 * — et retombent exactement sur le rail une fois l'écriture passée. Compté
 * en entier, l'écart du point de vente est compté MOINS annoncé, comme la
 * feuille : la ligne Espèces « virtuelle » d'une salle sans espèces au
 * rapport n'a pas de source, et l'écart ligne à ligne l'oubliait (02/10,
 * Bar ROOM 2 : −907 € dans les cases, +8 € au rail).
 *
 * ═══ L'ÉTAPE « ÉCARTS » LIT LA FEUILLE ═══
 *
 * `rapprochementsDeLaNuit` : source hors offerts, constaté comptage compris,
 * delta seulement une fois le point de vente compté en entier — les chiffres
 * de la feuille et du rail. L'ancienne règle (offerts dans la source,
 * comptage ignoré, delta sur un point de vente à moitié compté) préremplissait
 * puis ENREGISTRAIT un écart de −263 € là où la feuille dit +22 € (03/10).
 *
 * ═══ L'ANNONCÉ EST L'ATTENDU (08/10/2026) ═══
 *
 * À l'Accueil, une vente du guichet faite pendant une autre nuit, une fois
 * décidée, corrige ce que la caisse devait contenir sans réécrire la source :
 * le serveur sert `expected_cents` (source + transferts) à côté de
 * `source_cents`. Le rail, les cases du haut et les écarts lisent l'ATTENDU
 * (`attenduDe`) — le 03/10, 3 400 € + 1 060 € rattachés = 4 460 €, contre
 * 4 459 € comptés : −1 €, pas +1 059 €. Un serveur d'avant ce champ rend la
 * source, comme avant.
 *
 * ═══ LE MOUVEMENT SE DÉCIDE ICI AUSSI ═══
 *
 * Quelle pastille vient de s'allumer, dans quel sens glisse le contenu :
 * deux décisions pures, testées sans navigateur (`etapesAllumees`,
 * `sensDuPassage`). L'écran ne fait que poser la classe.
 */

import type { ClosingDetail, ClosingPosView } from './ClosingCommon.js'
import type { Cents } from './money.js'
import { feuilleDeCaisse, ligneDeCloture, type EtatLigne } from './night-sheet-model.js'

export interface EtapeAssistant {
  cle: string
  label: string
  /** L'identifiant de la ligne de périmètre, pour une étape de point de vente. */
  posId?: string
}

export type EtatEtape = 'faite' | 'en-cours' | 'a-faire' | 'ecart' | 'facultative'

/** Ce qu'on écrit sous une étape. Les montants restent des CENTIMES : le rail les formate. */
export type DetailEtape =
  | { genre: 'texte'; texte: string }
  | {
    genre: 'pos'; annonce: number | null; constate: number | null; ecart: number | null; partiel: boolean
    /** Ce qui reste à faire à cette étape hors de l'argent — la guestlist, à l'Accueil. */
    note?: string
  }

export interface EtapeRail extends EtapeAssistant {
  etat: EtatEtape
  detail: DetailEtape | null
  /**
   * Sur l'étape « Écarts » seulement : combien de FAITS restent à expliquer —
   * les écarts ouverts, plus les points de vente au-delà du seuil qu'aucun
   * écart ne couvre encore. Un écart ouvert rattaché à un bar met DEUX étapes
   * en « ! » (le bar et « Écarts ») : compter les étapes annonçait deux écarts
   * pour un seul (revue du 07/10/2026).
   */
  aExpliquer?: number
}

/**
 * Somme qui rend `null` tant qu'AUCUNE valeur n'est renseignée — la règle de
 * `sommeSiRenseignee` (ClosingCommon), recopiée pour que ce modèle reste
 * importable hors navigateur. Un point de vente sans source n'a pas « 0,00 €
 * de source » : il n'a PAS de source, et l'écart calculé contre un zéro
 * inventé serait un écart inventé.
 */
function sommeConnue(valeurs: Cents[]): number | null {
  let total = 0
  let connue = false
  for (const v of valeurs) {
    if (v === null || v === undefined) continue
    const n = typeof v === 'string' ? Number(v) : v
    if (!Number.isFinite(n)) continue
    total += n
    connue = true
  }
  return connue ? total : null
}

const enNombre = (v: Cents): number | null => {
  if (v === null || v === undefined) return null
  const n = typeof v === 'string' ? Number(v) : v
  return Number.isFinite(n) ? n : null
}

type LigneCloture = ClosingDetail['pos'][number]['lines'][number]

/** L'attendu d'une ligne : la source corrigée des ventes d'une autre nuit décidées ; à défaut, la source. */
export const attenduDe = (l: Pick<LigneCloture, 'source_cents' | 'expected_cents'>): Cents =>
  l.expected_cents !== undefined ? l.expected_cents : l.source_cents

/** Les lignes qui sont de la recette : tout sauf les offerts. */
const recette = (lignes: LigneCloture[]): LigneCloture[] => lignes.filter((l) => l.payment_method !== 'OFFERT')

/**
 * Annoncé, compté et écart d'un ensemble de lignes, hors offerts. L'écart ne
 * porte que sur les lignes à la fois annoncées et comptées ; `null` tant
 * qu'aucune ne l'est. `complet` = chaque ligne est comptée.
 */
export function chiffresLignes(lignes: LigneCloture[]): {
  annonce: number | null; constate: number | null; ecart: number | null
  comptees: number; total: number; complet: boolean
} {
  const r = recette(lignes)
  let ecart = 0
  let avecEcart = false
  let comptees = 0
  for (const l of r) {
    const o = enNombre(l.observed_cents)
    if (o === null) continue
    comptees += 1
    const s = enNombre(attenduDe(l))
    if (s === null) continue
    ecart += o - s
    avecEcart = true
  }
  return {
    annonce: sommeConnue(r.map(attenduDe)),
    constate: sommeConnue(r.map((l) => l.observed_cents)),
    ecart: avecEcart ? ecart : null,
    comptees, total: r.length, complet: r.length > 0 && comptees === r.length,
  }
}

/** Les quatre chiffres du bandeau, sur les points de vente ouverts — mêmes règles que le rail. */
export function chiffresCloture(d: ClosingDetail): ReturnType<typeof chiffresLignes> {
  return chiffresLignes(d.pos.filter((p) => p.was_open === true).flatMap((p) => p.lines))
}

function pluriel(n: number, un: string, plusieurs: string): string {
  return `${n} ${n > 1 ? plusieurs : un}`
}

const texte = (t: string): DetailEtape => ({ genre: 'texte', texte: t })

/**
 * L'état de chaque étape, d'après la clôture.
 *
 * Les seuils viennent des réglages, les mêmes que l'étape « Écarts » : ils
 * colorent l'écart, et seul le seuil d'ALERTE fait lever le « ! » quand rien
 * n'explique encore l'écart. En dessous, il se lit dans la synthèse sans
 * réclamer d'explication — un rail qui crie pour 2 € n'est plus écouté.
 *
 * Un écart n'est pas une faute : le « ! » signale un fait comptable à
 * expliquer, AU POINT DE VENTE — jamais une personne, jamais un terminal
 * contre une caisse.
 */
export function etatsDesEtapes(
  etapes: EtapeAssistant[], d: ClosingDetail, courante: string, seuils: { warn: number; alert: number },
): EtapeRail[] {
  const ouverts = d.pos.filter((p) => p.was_open === true)
  const sansReponse = d.pos.filter((p) => p.was_open === null).length
  const figee = d.status === 'CLOSED' || d.status === 'SUPERSEDED'

  /* Le verdict de chaque point de vente, calculé une fois. */
  /* Le verdict de chaque point de vente : LA MÊME règle que la feuille de
     caisse et le bandeau (`ligneDeCloture`, night-sheet-model). Ligne à
     ligne, une salle dont le rapport n'a pas de ligne « Espèces » perdait
     ses espèces comptées (02/10 : 915 € en coupures ignorés, −907 € au rail
     contre +8 € sur la feuille). L'écart n'existe qu'une fois le point de
     vente compté en entier ; avant, « compté en partie », sans alerte. */
  const parPos = new Map(ouverts.map((p) => {
    const l = ligneDeCloture(p)
    const attaches = d.discrepancies.filter((e) => e.closing_pos_id === p.id)
    const ouvert = attaches.some((e) => e.status === 'OUVERT')
    const traite = attaches.some((e) => e.status !== 'OUVERT')
    const complet = l.etat === 'compte'
    const aExpliquer = ouvert || (p.step_done && complet && l.ecart !== null && Math.abs(l.ecart) >= seuils.alert && !traite)
    // Compté, mais des espèces qu'aucune ligne ne porte : l'écart se lit (c'est
    // celui de la feuille), l'étape n'est pas faite — le report reste à faire.
    return [p.id, {
      fait: p.step_done && complet && !l.especesAReporter, source: l.rapport, constate: l.compte, delta: l.ecart,
      partiel: l.etat === 'partiel', aExpliquer, aReporter: l.especesAReporter,
    }] as const
  }))

  /* Une vente du guichet d'une autre nuit qui attend sa décision retient
     l'étape de l'Accueil : la finalisation l'attend aussi (le serveur le
     refuse, `obstaclesFinalisation`). */
  const horsNuitEnAttente = (d.off_night?.cases ?? []).filter((c) => c.blocking).length
  const estAccueil = (posId: string): boolean => d.off_night?.accueil_pos_id != null
    ? d.off_night.accueil_pos_id === posId
    : d.pos.find((p) => p.id === posId)?.point_of_sale_kind === 'BILLETTERIE'

  const ecartsOuverts = d.discrepancies.filter((e) => e.status === 'OUVERT').length
  const posAExpliquer = [...parPos.values()].filter((v) => v.aExpliquer).length
  // Les faits à expliquer, chacun une fois : un point de vente déjà couvert
  // par un écart OUVERT est compté par cet écart, pas une seconde fois.
  const posSansEcartOuvert = ouverts.filter((p) => parPos.get(p.id)?.aExpliquer
    && !d.discrepancies.some((e) => e.closing_pos_id === p.id && e.status === 'OUVERT')).length
  const aExpliquer = ecartsOuverts + posSansEcartOuvert
  const tousComptes = ouverts.length > 0 && ouverts.every((p) => parPos.get(p.id)?.fait === true)

  // L'écart et la réalisation priment ; « en cours » ne qualifie que l'étape
  // affichée qui n'est pas encore faite.
  const finir = (e: EtapeAssistant, base: EtatEtape, detail: DetailEtape | null): EtapeRail => ({
    ...e, etat: base === 'a-faire' && e.cle === courante ? 'en-cours' : base, detail,
  })

  return etapes.map((e): EtapeRail => {
    if (e.cle === 'nuit') {
      if (d.pos.length === 0) return finir(e, 'a-faire', texte('Périmètre à déclarer'))
      if (sansReponse > 0) {
        return finir(e, 'a-faire', texte(`${pluriel(sansReponse, 'point de vente', 'points de vente')} sans réponse`))
      }
      return finir(e, 'faite', texte(ouverts.length === 0
        ? 'Aucun point de vente ouvert'
        : pluriel(ouverts.length, 'point de vente ouvert', 'points de vente ouverts')))
    }

    if (e.posId !== undefined) {
      const v = parPos.get(e.posId)
      if (!v) return finir(e, 'a-faire', null)
      const detail: Extract<DetailEtape, { genre: 'pos' }> = { genre: 'pos', annonce: v.source, constate: v.constate, ecart: v.delta, partiel: v.partiel }
      // La guestlist se saisit à l'Accueil : le rail le rappelle tant qu'elle
      // manque. Une information, pas un état — elle ne retient pas l'étape
      // « à faire », la finalisation ne l'exige pas. Les espèces à reporter,
      // elles, retiennent l'étape : le rail dit pourquoi.
      const notes: string[] = []
      if (!figee && v.aReporter) notes.push('espèces à reporter')
      if (!figee && d.entries && d.entries.guestlist_count === null
        && d.pos.find((p) => p.id === e.posId)?.point_of_sale_kind === 'BILLETTERIE') {
        notes.push('guestlist à saisir')
      }
      const aDecider = !figee && horsNuitEnAttente > 0 && estAccueil(e.posId)
      if (aDecider) notes.push(horsNuitEnAttente > 1 ? `${horsNuitEnAttente} ventes d’une autre nuit à décider` : 'vente d’une autre nuit à décider')
      if (notes.length > 0) detail.note = notes.join(' · ')
      if (v.aExpliquer) return finir(e, 'ecart', detail)
      return finir(e, v.fait && !aDecider ? 'faite' : 'a-faire', detail)
    }

    if (e.cle === 'ecarts') {
      const avecCompte = (r: EtapeRail): EtapeRail => ({ ...r, aExpliquer })
      if (ecartsOuverts > 0) return avecCompte(finir(e, 'ecart', texte(pluriel(ecartsOuverts, 'écart ouvert', 'écarts ouverts'))))
      if (posAExpliquer > 0) return avecCompte(finir(e, 'ecart', texte(`${pluriel(posAExpliquer, 'écart', 'écarts')} à expliquer`)))
      if (!tousComptes && !figee) return avecCompte(finir(e, 'a-faire', texte('Après le comptage')))
      const traites = d.discrepancies.length
      return avecCompte(finir(e, 'faite', texte(traites === 0 ? 'Aucun écart à expliquer' : pluriel(traites, 'écart traité', 'écarts traités'))))
    }

    if (e.cle === 'recap') return { ...e, etat: 'facultative', detail: texte('Facultatif · annexes de la nuit') }

    if (e.cle === 'synthese') {
      if (d.status === 'CLOSED') return finir(e, 'faite', texte('Nuit finalisée'))
      if (d.status === 'SUPERSEDED') return finir(e, 'faite', texte('Révision remplacée'))
      if (d.blockers.length === 0) return finir(e, 'a-faire', texte('Prête à finaliser'))
      return finir(e, 'a-faire', texte(`${pluriel(d.blockers.length, 'point à régler', 'points à régler')} avant de finaliser`))
    }

    return finir(e, 'a-faire', null)
  })
}

/**
 * La progression : les étapes faites sur celles qui comptent (le récap est
 * facultatif). « À expliquer » compte des FAITS, pas des étapes : le nombre
 * que l'étape « Écarts » porte ; à défaut, les points de vente en « ! ».
 */
export function progression(etapes: EtapeRail[]): { faites: number; total: number; part: number; aExpliquer: number } {
  const comptees = etapes.filter((e) => e.etat !== 'facultative')
  const faites = comptees.filter((e) => e.etat === 'faite').length
  const recap = etapes.find((e) => e.cle === 'ecarts')
  return {
    faites,
    total: comptees.length,
    part: comptees.length === 0 ? 0 : Math.round((faites / comptees.length) * 100),
    aExpliquer: recap?.aExpliquer ?? etapes.filter((e) => e.etat === 'ecart' && e.cle !== 'ecarts').length,
  }
}

/* ═══════════════ les cases du haut d'un point de vente, en direct ═══════════════ */

/**
 * Ce que l'étape tient EN MAIN, pas encore (ou pas tout à fait) enregistré.
 * Un champ absent = on s'en remet au serveur.
 */
export interface SaisieEnDirect {
  /** Les constatés tapés dans le rapprochement, par ligne ; absent = celui du serveur. */
  constates?: Readonly<Record<string, number | null>>
  /** La somme des tickets TPE chiffrés à l'écran ; `null` = aucun ticket chiffré. */
  tickets?: number | null
  /** Le comptage des espèces à l'écran (coupures, ou total saisi directement) ; `null` = rien de compté. */
  comptage?: number | null
}

/**
 * D'où vient un constaté : la somme des tickets TPE, une saisie sur la ligne
 * du rapprochement, ou le comptage des espèces pas encore reporté.
 */
export type OrigineConstate = 'tickets' | 'saisie' | 'comptage' | null

export interface ChiffresMoyen {
  /** Le code du moyen de paiement (`CB`, `ESPECES`, ou un autre code du référentiel). */
  code: string
  /** Le libellé du référentiel quand une ligne le porte ; à défaut, celui de l'écran. */
  libelle: string | null
  annonce: number | null
  constate: number | null
  ecart: number | null
  origine: OrigineConstate
}

export interface ChiffresPointDeVente {
  /** Carte et Espèces toujours, dans cet ordre ; puis les autres moyens présents. */
  moyens: ChiffresMoyen[]
  /** La source seule, hors offerts (le guichet, le rapport) ; `null` sans source. */
  source: number | null
  /** Ce que les ventes d'une autre nuit décidées ajoutent (+) ou retirent (−) à l'annoncé ; 0 ailleurs qu'à l'Accueil. */
  transfert: number
  /** Les offerts annoncés : une information, hors de tout total. */
  offerts: number | null
  annonce: number | null
  constate: number | null
  ecart: number | null
  comptees: number
  total: number
  complet: boolean
  /** Au moins une ligne comptée, pas toutes : « en partie ». */
  partiel: boolean
  /**
   * Les espèces comptées ne figurent encore sur AUCUNE ligne : le chiffre est
   * montré (c'est ce que la personne a compté), mais tant qu'il n'est pas
   * reporté, le rail et le retenu ne le connaissent pas. L'écran le dit.
   */
  comptageNonReporte: boolean
}

/** La ligne qui reçoit un constaté global (tickets, comptage) : celle qui porte la source, à défaut la première — la règle du serveur (`saveTpeTickets`). */
function lignePrincipale(lignes: LigneCloture[], code: string): LigneCloture | null {
  return lignes.find((l) => l.payment_method === code && enNombre(l.source_cents) !== null)
    ?? lignes.find((l) => l.payment_method === code) ?? null
}

/**
 * Une ligne que le serveur n'a pas encore, mais qu'il aura : des tickets TPE
 * enregistrés sur un point de vente sans ligne « Carte » la créent
 * (`saveTpeTickets`) ; un comptage sans ligne « Espèces » entre quand même
 * dans l'écart que le serveur propose (`computeDiscrepancyProposals`).
 */
function ligneVirtuelle(code: string, constate: number): LigneCloture {
  return {
    id: `virtuelle-${code}`, closing_pos_id: null, register_id: null, register_name: null,
    terminal_id: null, terminal_name: null, payment_method: code, payment_method_label: code,
    source_cents: null, source_reading_id: null, observed_cents: constate, retained_cents: null,
    transfer_cents: 0, expected_cents: null,
    effective_cents: null, effective_origin: 'OBSERVED', override_reason: null, override_by_name: null, override_at: null,
  }
}

/**
 * Les chiffres d'un point de vente — annoncé, compté, écart, moyen par moyen
 * et au total —, avec ce qui est EN TRAIN d'être saisi.
 *
 * Les règles sont celles du rail et du bandeau, par construction : la saisie
 * est d'abord posée sur les lignes, puis `chiffresLignes` fait le reste
 * (offerts hors de tout, écart sur les seules lignes comptées, « — » tant que
 * rien ne l'est). Ce qui s'ajoute :
 *
 *   · Carte — la somme des tickets TPE chiffrés, sur la ligne principale ;
 *     sans ticket, ce qui est tapé dans le rapprochement ;
 *   · Espèces — ce que porte la ligne si quelqu'un l'a renseignée ; sinon le
 *     comptage, s'il existe, signalé « pas encore reporté ». C'est la règle
 *     de l'écart que le serveur propose à l'étape « Écarts ».
 */
export function chiffresPointDeVente(lignes: LigneCloture[], saisie: SaisieEnDirect = {}): ChiffresPointDeVente {
  const constates = saisie.constates ?? {}
  const tickets = saisie.tickets ?? null
  const comptage = saisie.comptage ?? null

  const carte = lignePrincipale(lignes, 'CB')
  let enDirect: LigneCloture[] = lignes.map((l) => {
    if (l.payment_method === 'OFFERT') return l
    if (tickets !== null && carte !== null && l.id === carte.id) return { ...l, observed_cents: tickets }
    return l.id in constates ? { ...l, observed_cents: constates[l.id] ?? null } : l
  })
  if (tickets !== null && carte === null) enDirect = [...enDirect, ligneVirtuelle('CB', tickets)]

  const especesSaisies = enDirect.some((l) => l.payment_method === 'ESPECES' && enNombre(l.observed_cents) !== null)
  const comptageNonReporte = !especesSaisies && comptage !== null
  if (comptageNonReporte) {
    const principale = lignePrincipale(enDirect, 'ESPECES')
    enDirect = principale
      ? enDirect.map((l) => (l.id === principale.id ? { ...l, observed_cents: comptage } : l))
      : [...enDirect, ligneVirtuelle('ESPECES', comptage)]
  }

  const moyen = (code: string, origine: OrigineConstate): ChiffresMoyen => {
    const ls = enDirect.filter((l) => l.payment_method === code)
    const c = chiffresLignes(ls)
    const reelle = ls.find((l) => !l.id.startsWith('virtuelle-'))
    return {
      code, libelle: reelle?.payment_method_label ?? null,
      annonce: c.annonce, constate: c.constate, ecart: c.ecart,
      origine: c.constate === null ? null : origine,
    }
  }
  const autres = [...new Set(enDirect.map((l) => l.payment_method))]
    .filter((m) => m !== 'CB' && m !== 'ESPECES' && m !== 'OFFERT')
  const c = chiffresLignes(enDirect)
  // Compté en entier : l'écart du POINT DE VENTE, compté moins annoncé — la
  // règle de la feuille et du rail (`ligneDeCloture`). Ligne à ligne, une
  // ligne comptée sans source (les espèces d'une salle dont le rapport n'en
  // annonce pas) entrait dans le compté et sortait de l'écart. En partie,
  // l'écart reste celui des lignes déjà comptées, et le dit.
  const ecart = c.complet && c.annonce !== null && c.constate !== null ? c.constate - c.annonce : c.ecart
  const recetteServeur = lignes.filter((l) => l.payment_method !== 'OFFERT')
  return {
    moyens: [
      moyen('CB', tickets !== null ? 'tickets' : 'saisie'),
      moyen('ESPECES', comptageNonReporte ? 'comptage' : 'saisie'),
      ...autres.map((m) => moyen(m, 'saisie')),
    ],
    source: sommeConnue(recetteServeur.map((l) => l.source_cents)),
    transfert: recetteServeur.reduce((t, l) => t + (enNombre(l.transfer_cents ?? null) ?? 0), 0),
    offerts: sommeConnue(lignes.filter((l) => l.payment_method === 'OFFERT').map((l) => l.source_cents)),
    annonce: c.annonce, constate: c.constate, ecart,
    comptees: c.comptees, total: c.total, complet: c.complet,
    partiel: c.comptees > 0 && !c.complet,
    comptageNonReporte,
  }
}

/* ═══════════════════ l'étape « Écarts » : les chiffres de la feuille ═══════════════════ */

/** Une ligne du tableau « Écarts de la nuit » : un périmètre, ses montants, son écart. */
export interface RapprochementEcart {
  /** La ligne de périmètre ; `null` = la nuit entière. */
  pos: ClosingPosView | null
  titre: string
  lieu: string | null
  /** Ce que le rapport annonce, HORS OFFERTS. */
  source: number | null
  /** Ce qui est compté, comptage d'espèces compris (la feuille de caisse). */
  constate: number | null
  /** Le retenu explicite des lignes (une décision motivée), offerts compris : ce qui sera arrêté tel quel. */
  retenu: number | null
  /** `null` tant que le périmètre n'est pas compté en entier, ou sans rapport. Ce n'est PAS un écart nul. */
  delta: number | null
  /** L'état de la ligne de la feuille (`a-compter`, `partiel`, `compte`…) ; pour la nuit, `compte` quand tout l'est. */
  etat: EtatLigne
  /** Pour la nuit : sur combien de points de vente porte un delta partiel. */
  comptes: number
  total: number
}

/**
 * LES LIGNES DE L'ÉTAPE « ÉCARTS » — celles de la feuille de caisse, point de
 * vente par point de vente, puis la nuit. Le delta est celui que l'étape
 * PRÉREMPLIT dans « Expliquer l'écart » : il doit être l'écart de la feuille,
 * hors offerts, sinon le registre des écarts enregistre un montant faux.
 * La nuit n'a de delta qu'une fois CHAQUE point de vente ouvert compté en
 * entier (et aucun sans réponse) : un total partiel préremplirait un écart de
 * nuit qui n'en est pas un.
 */
export function rapprochementsDeLaNuit(d: ClosingDetail): RapprochementEcart[] {
  const ouverts = d.pos.filter((p) => p.was_open === true)
  const lignes = ouverts.map((p): RapprochementEcart => {
    const l = ligneDeCloture(p)
    return {
      pos: p, titre: p.point_of_sale_name, lieu: p.location_name || null,
      source: l.rapport, constate: l.compte,
      retenu: sommeConnue(p.lines.map((x) => x.retained_cents)),
      delta: l.ecart, etat: l.etat,
      comptes: l.ecart === null ? 0 : 1, total: 1,
    }
  })
  const f = feuilleDeCaisse(d, null)
  lignes.push({
    pos: null, titre: 'Toute la nuit', lieu: null,
    source: f.total.rapport, constate: f.total.compte,
    retenu: sommeConnue(lignes.map((r) => r.retenu)),
    delta: f.complet && f.total.comptes === f.total.total ? f.total.ecart : null,
    etat: f.complet ? 'compte' : f.total.compte === null ? 'a-compter' : 'partiel',
    comptes: f.total.comptes, total: f.total.total,
  })
  return lignes
}

/**
 * L'ampleur d'un écart, pour sa couleur — les seuils des réglages, ceux de
 * l'étape « Écarts » et de `Ecart` (ClosingCommon) : sous l'avertissement,
 * dans la tolérance ; au-delà de l'alerte, à expliquer. Le SIGNE n'y entre
 * pas : 200 € de trop ne s'expliquent pas moins que 200 € de moins.
 */
export type NiveauEcart = 'aucun' | 'ok' | 'tiede' | 'fort'

export function niveauEcart(ecart: number | null, seuils: { warn: number; alert: number }): NiveauEcart {
  if (ecart === null) return 'aucun'
  const a = Math.abs(ecart)
  return a >= seuils.alert ? 'fort' : a >= seuils.warn ? 'tiede' : 'ok'
}

/* ═══════════════════════ le mouvement, décidé ═══════════════════════ */

/**
 * Les étapes qui viennent de passer « faites » — et elles seules : une étape
 * déjà faite au chargement ne s'allume pas (l'entrée de la page suffit), et
 * une étape qu'on ne connaissait pas encore non plus.
 */
export function etapesAllumees(avant: ReadonlyMap<string, EtatEtape> | null, apres: readonly EtapeRail[]): string[] {
  if (!avant) return []
  return apres.filter((e) => e.etat === 'faite' && avant.has(e.cle) && avant.get(e.cle) !== 'faite').map((e) => e.cle)
}

/**
 * Le sens du passage d'une étape à l'autre : le contenu glisse vers la
 * gauche quand on avance, vers la droite quand on recule ; rien au premier
 * affichage, ni quand l'étape ne change pas.
 */
export function sensDuPassage(avant: number | null, apres: number): 'avant' | 'arriere' | null {
  if (avant === null || avant < 0 || apres < 0 || avant === apres) return null
  return apres > avant ? 'avant' : 'arriere'
}
